import { useRef, useState, useEffect, RefObject } from 'react';

/**
 * Calculates the maximum height for an element based on its position
 * relative to the bottom of the viewport.
 *
 * @param data The data that, when changed, should trigger a recalculation.
 * @param buffer Optional buffer space (in pixels) to leave below the element. Defaults to 20.
 * @param minHeight Optional minimum height (in pixels) for the element. Defaults to 100.
 * @returns An object containing:
 *  - `ref`: A RefObject to attach to the target DOM element.
 *  - `maxHeight`: The calculated maximum height string (e.g., "500px").
 */
export function useDynamicMaxHeight(
  data: any,
  buffer = 20,
  minHeight = 100
): {
  ref: RefObject<HTMLDivElement>;
  maxHeight: string;
} {
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<string>('100vh'); // Start with full viewport height initially

  useEffect(() => {
    const calculateMaxHeight = () => {
      try {
        if (!ref.current) {
          return;
        }
        const rect = ref.current.getBoundingClientRect();
        const viewportHeight =
          typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 800;
        const availableHeight = viewportHeight - rect.top - buffer;
        setMaxHeight(`${Math.max(minHeight, availableHeight)}px`);
      } catch (e) {
        // Fall back silently on environments with restricted measurement
        setMaxHeight(`${minHeight}px`);
      }
    };

    // Calculate initially
    if (typeof requestAnimationFrame === 'function') {
      const rafId = requestAnimationFrame(calculateMaxHeight);
      // Recalculate on window resize
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', calculateMaxHeight);
      }
      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('resize', calculateMaxHeight);
        }
        cancelAnimationFrame(rafId);
      };
    } else {
      // No RAF (older Safari/iOS WKWebView edge cases) – compute synchronously
      calculateMaxHeight();
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', calculateMaxHeight);
        return () => window.removeEventListener('resize', calculateMaxHeight);
      }
      return () => {};
    }
    // Dependencies: buffer, minHeight, and data.
  }, [data, buffer, minHeight]);

  return { ref, maxHeight };
}

export default useDynamicMaxHeight;
